import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { StartupHelperStateService } from '../services/startup-helper-state.service';
import { AuthStore } from '../store/auth.store';
import { ToastService } from './toast.service';
import {
  STARTUP_HELPER_FILE_NAME,
  canOfferStartupHelper,
  downloadStartupHelper,
} from '../utils/startup-helper';

/**
 * Always-available "Start JioPC with Windows" button for signed-in users, next to the one-time
 * Login/Dashboard card and the Profile link (see startup-helper.ts). Gates itself on
 * `canOfferStartupHelper()` (switch on, installed app, Windows, Chrome/Edge), so hosts just mount it.
 * Hidden once the helper reports success (`setup:`), live; a download alone keeps it — the user
 * may not have run the file, and this is how they get it again (StartupHelperStateService).
 *
 * - `header`: round icon button matching the dashboard header's Help/Exit buttons.
 * - `titlebar`: compact labelled button in the installed app's window-controls-overlay title-bar
 *   strip, only after sign-in. Mounted once in app.ts so it rides along on every route. Hidden
 *   by CSS outside `display-mode: window-controls-overlay` (standalone, fullscreen / a running
 *   VDI session), where that strip doesn't exist. `app-region: no-drag` keeps it clickable if a
 *   draggable title bar is ever added (styles.css documents that none exists yet).
 */
@Component({
  selector: 'app-startup-helper-button',
  standalone: true,
  imports: [TranslatePipe],
  styles: `
    .titlebar-slot {
      display: none;
    }
    @media (display-mode: window-controls-overlay) {
      .titlebar-slot {
        display: flex;
        position: fixed;
        top: var(--app-titlebar-area-y);
        left: calc(var(--app-titlebar-area-x) + 8px);
        height: var(--app-titlebar-area-height);
        align-items: center;
        z-index: 70;
        -webkit-app-region: no-drag;
        app-region: no-drag;
      }
    }
  `,
  template: `
    @if (visible()) {
      @if (variant() === 'titlebar') {
        <div class="titlebar-slot">
          <button
            type="button"
            (click)="download()"
            [attr.title]="'dashboard.myPc.startupHelper.tooltip' | translate"
            class="flex h-6 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-white/80 transition-colors hover:cursor-pointer hover:bg-white/10 hover:text-white"
          >
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v9m6.36-5.36a9 9 0 1 1-12.72 0" />
            </svg>
            {{ 'dashboard.myPc.startupHelper.button' | translate }}
          </button>
        </div>
      } @else {
        <button
          type="button"
          (click)="download()"
          [attr.aria-label]="'dashboard.myPc.startupHelper.tooltip' | translate"
          [attr.title]="'dashboard.myPc.startupHelper.tooltip' | translate"
          class="hover:cursor-pointer flex h-10 w-10 items-center justify-center rounded-full bg-[#281240] text-white transition-colors hover:bg-purple-card-light opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v9m6.36-5.36a9 9 0 1 1-12.72 0" />
          </svg>
        </button>
      }
    }
  `,
})
export class StartupHelperButtonComponent {
  private readonly state = inject(StartupHelperStateService);
  private readonly auth = inject(AuthStore);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);

  readonly variant = input<'header' | 'titlebar'>('header');

  private readonly offered = canOfferStartupHelper();
  readonly visible = computed(
    () =>
      this.offered &&
      (this.variant() !== 'titlebar' || this.auth.isAuthenticated()) &&
      !this.state.isSetUp(),
  );

  /** Same persistence as the card and Profile link, so none of them asks again for this version. */
  download(): void {
    downloadStartupHelper();
    this.state.recordDownload();
    this.toast.info(
      this.translate.instant('dashboard.myPc.startupHelper.downloadedTitle'),
      this.translate.instant('dashboard.myPc.startupHelper.downloadedBody', {
        fileName: STARTUP_HELPER_FILE_NAME,
      }),
    );
  }
}
